import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api, ApiError } from '@/services/api';
import { Test, Task } from '@/types';
import GroupedContent from '@/shared/ui/layout/GroupedContent';
import './styles.css';

type TestFormData = Omit<Test, '_id' | 'createdAt' | 'updatedAt'>;

// Helper function to group tasks by category
const groupTasksByCategory = (tasks: Task[]) => {
  const grouped: Record<string, Task[]> = {};
  
  tasks.forEach(task => {
    const category = task.category || 'Uncategorized';
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(task);
  });
  
  // Sort categories alphabetically
  return Object.keys(grouped)
    .sort()
    .reduce((acc, category) => {
      acc[category] = grouped[category];
      return acc;
    }, {} as Record<string, Task[]>);
};

const TestForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<TestFormData>({
    name: '',
    description: '',
    priority: 500,
    tasks: []
  });
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [groupedTasks, setGroupedTasks] = useState<Record<string, Task[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch available tasks
        const tasks = await api.tasks.getAll();
        setAvailableTasks(tasks);
        setGroupedTasks(groupTasksByCategory(tasks));

        // If editing, fetch test data
        if (isEditMode && id) {
          const test = await api.tests.getById(id);
          setFormData({
            name: test.name,
            description: test.description,
            priority: test.priority ?? 500,
            tasks: test.tasks
          });
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEditMode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTaskToggle = (taskId: string) => {
    setFormData(prev => {
      const tasks = Array.isArray(prev.tasks) ? prev.tasks : [];
      const taskIds = tasks.map((t: string | Task) => 
        typeof t === 'string' ? t : t._id!
      ).filter((id): id is string => id !== undefined);
      
      if (taskIds.includes(taskId)) {
        // Remove task
        return {
          ...prev,
          tasks: taskIds.filter(id => id !== taskId)
        };
      } else {
        // Add task
        return {
          ...prev,
          tasks: [...taskIds, taskId]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      if (isEditMode && id) {
        const originalTest = await api.tests.getById(id);
        const priorityChanged = formData.priority !== originalTest.priority;
        const otherFieldsChanged =
          formData.name !== originalTest.name ||
          formData.description !== originalTest.description ||
          JSON.stringify(formData.tasks.map((t: any) => t._id || t).sort()) !== JSON.stringify(originalTest.tasks.map((t: any) => t._id || t).sort());

        if (priorityChanged && !otherFieldsChanged) {
          await api.tests.updatePriority(id, formData.priority as number);
        } else {
          await api.tests.update(id, formData);
        }
        toast.success('Test updated successfully!');
      } else {
        await api.tests.create(formData);
        toast.success('Test created successfully!');
      }
      navigate('/tests');
    } catch (err) {
      const errorMessage = (err as ApiError).message || `Failed to ${isEditMode ? 'update' : 'create'} test`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Render function for task groups
  const renderTaskGroup = (category: string, tasks: Task[]) => (
    <div className="tasks-grid">
      {tasks.map(task => {
        const isSelected = Array.isArray(formData.tasks) && 
          formData.tasks.some(t => 
            (typeof t === 'string' ? t : t._id) === task._id
          );

        return (
          <div 
            key={task._id} 
            className={`task-card ${isSelected ? 'selected' : ''}`}
            onClick={() => task._id && handleTaskToggle(task._id)}
          >
            <h3>{task.title}</h3>
            <p className="task-excerpt">{task.content}</p>
            <div className="task-meta">
              <div className="status-badge" data-status={task.scoringMethod}>
                {task.scoringMethod === 'numerical' ? 'Numerical Score' : 'Pass/Fail'}
              </div>
              {task.scoringMethod === 'numerical' && task.scoringScale && (
                <span className="scoring-scale">
                  Scale: {task.scoringScale.min} - {task.scoringScale.max}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  if (loading && !formData.name) return <div>Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h1>{isEditMode ? 'Edit Test' : 'Create New Test'}</h1>
      </div>
      <div className="page-content">
        <form onSubmit={handleSubmit} className="test-form">
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <input
              type="number"
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              min="0"
              max="1000"
              required
            />
            <small>Lower number means higher priority (0-1000).</small>
          </div>

          <div className="form-group">
            <label>Tasks</label>
            <div className="tasks-selection">
              {Object.keys(groupedTasks).length === 0 ? (
                <p className="no-tasks">No tasks available. Create some tasks first.</p>
              ) : (
                <GroupedContent
                  groups={groupedTasks}
                  renderGroup={renderTaskGroup}
                  emptyMessage="No tasks available. Create some tasks first."
                />
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={() => navigate('/tests')} className="button">
              Cancel
            </button>
            <button type="submit" className="button primary" disabled={loading}>
              {loading ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestForm; 